// Decides what the White Balance block on the test review screen shows.
//
// The backend owns every rule here: whether a test may be converted, how a
// conversion ended, the words for a failure and how often to ask again. This
// file reads what a response reports and decides how to present it, with a
// fallback for each field a backend might not send. Text from the backend is
// passed through as received, apart from a full stop in front of a separator.
//
// Pure: no component, store, request or clock. The caller passes the current
// time in, so the elapsed figure and every state can be checked without the
// app running.

// Stands in for a response with no usable `pollAfterSeconds`. It is the
// backend's own default, so a block reading a backend that omits the field
// polls at the pace that backend would have asked for.
const FALLBACK_POLL_AFTER_SECONDS = 15;

const LOADING_TEXT = "Checking white balance…";
const REQUEST_TITLE = "White balance";
const REQUEST_TEXT = "A colour-corrected copy for yellowish or dark videos";
const REQUEST_BUTTON = "Request";
const REQUESTING_BUTTON = "Requesting…";
const PROCESSING_TITLE = "Preparing white balance…";
const TRY_AGAIN_BUTTON = "Try again";
const GENERIC_FAILURE = "White balance couldn't be prepared";
const STATUS_UNAVAILABLE_TEXT = "Couldn't load the white balance status.";
const LESS_THAN_A_MINUTE = "less than a minute";
const SEPARATOR = " · ";

const NOTICE_READY_TITLE = "White balance video ready";
const NOTICE_READY_TEXT = "It's under the original video";
const NOTICE_FAILED_TITLE = "White balance couldn't be prepared";
const NOTICE_FAILED_TEXT = "See the reason under the original video";

const FINAL_STATUSES = ["succeeded", "failed", "expired"];

function isObject(value) {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function usableText(value) {
  return typeof value === "string" && value.trim() !== "" ? value.trim() : null;
}

function joinParts(parts) {
  const present = parts.filter(Boolean);
  if (!present.length) {
    return null;
  }
  // Backend text arrives as sentences, and a sentence's full stop left in front
  // of the separator reads as a stray dot. The last part keeps its own, so a
  // part shown alone is exactly what the backend sent.
  const last = present.length - 1;
  return present
    .map((part, index) => (index < last ? withoutTrailingFullStops(part) : part))
    .filter(Boolean)
    .join(SEPARATOR);
}

function withoutTrailingFullStops(text) {
  return text.replace(/[.\s]+$/, "");
}

// A browser clock behind the server's makes the difference zero or negative,
// and a request that cannot be dated has no figure to give. Both read as the
// smallest wait, which is what the block shows a moment after a request.
export function elapsedText(requestedAt, nowMs) {
  const requestedMs = typeof requestedAt === "string" ? Date.parse(requestedAt) : NaN;
  if (!Number.isFinite(requestedMs) || !Number.isFinite(nowMs)) {
    return LESS_THAN_A_MINUTE;
  }
  const minutes = Math.floor((nowMs - requestedMs) / 60000);
  return minutes < 1 ? LESS_THAN_A_MINUTE : minutes + " min";
}

export function nextPollDelayMs(pollAfterSeconds) {
  const seconds =
    typeof pollAfterSeconds === "number" &&
    Number.isFinite(pollAfterSeconds) &&
    pollAfterSeconds > 0
      ? pollAfterSeconds
      : FALLBACK_POLL_AFTER_SECONDS;
  return seconds * 1000;
}

function view(state, fields) {
  return Object.assign(
    {
      state: state,
      card: null,
      title: null,
      text: null,
      button: null,
      videoUrl: null,
      pollDelayMs: null,
      unavailableText: null,
    },
    fields
  );
}

function ineligibleMessage(data) {
  const reason = isObject(data.ineligibleReason) ? data.ineligibleReason : {};
  return usableText(reason.message);
}

// An unrecognised status, or a success with no video to play, cannot be shown
// as anything the backend described, so it gets the generic title. On a test
// that may not be converted again there is no button, and the reason takes
// the place of the advice, which usually tells QC to try again. The reference
// is shown only here: QC quotes it when reporting a failure.
function failedView(conversion, failure, data) {
  const reason = isObject(failure) ? failure : {};
  const detail = data.eligible ? usableText(reason.advice) : ineligibleMessage(data);
  const reference = usableText(conversion.reference);
  return view("failed", {
    card: "failed",
    title: usableText(reason.message) || GENERIC_FAILURE,
    text: joinParts([detail, reference ? "Ref " + reference : null]),
    button: data.eligible ? { label: TRY_AGAIN_BUTTON, disabled: false } : null,
  });
}

function processingView(conversion, pollAfterSeconds, nowMs) {
  return view("processing", {
    card: "preparing",
    title: PROCESSING_TITLE,
    text: joinParts([
      elapsedText(conversion.requestedAt, nowMs) + " so far",
      "usually under 10 minutes",
      "you can keep working",
    ]),
    pollDelayMs: nextPollDelayMs(pollAfterSeconds),
  });
}

// The state a status body describes on its own. A conversion is shown
// whatever `eligible` says: a backend with the feature switched off, or with
// the test type taken off its list, still reports the test's last conversion,
// and hiding it would take away a video already made or stop following one
// still being made. Eligibility then decides only whether a button is offered.
function stateFromBody(body, nowMs) {
  const data = isObject(body) && isObject(body.data) ? body.data : {};

  // A backend that does not report eligibility is not one this block knows
  // how to read, so it stays out of the reviewer's way.
  if (typeof data.eligible !== "boolean") {
    return view("hidden");
  }

  const conversion = isObject(data.conversion) ? data.conversion : null;
  if (!conversion) {
    if (!data.eligible) {
      return view("notEligible", { text: ineligibleMessage(data) });
    }
    return view("notRequested", {
      card: "request",
      title: REQUEST_TITLE,
      text: REQUEST_TEXT,
      button: { label: REQUEST_BUTTON, disabled: false },
    });
  }

  switch (conversion.status) {
    case "processing":
      return processingView(conversion, data.pollAfterSeconds, nowMs);
    case "succeeded": {
      const videoUrl = usableText(conversion.videoUrl);
      if (!videoUrl) {
        return failedView(conversion, null, data);
      }
      return view("available", { videoUrl: videoUrl });
    }
    case "failed":
    case "expired":
      return failedView(conversion, conversion.failure, data);
    default:
      return failedView(conversion, null, data);
  }
}

/**
 * The one state the block shows.
 *
 * @param {object}  input
 * @param {?object} input.body       The latest body carrying a status: a
 *                                   resolved status read or request. Null
 *                                   before the first.
 * @param {boolean} input.notFound   A status read answered 404.
 * @param {boolean} input.readFailed The latest status read failed otherwise.
 * @param {boolean} input.requesting A request is in flight.
 * @param {number}  nowMs            Current time in milliseconds, taken when
 *                                   the body arrived.
 * @returns {{state: string, card: ?string, title: ?string, text: ?string,
 *            button: ?{label: string, disabled: boolean},
 *            videoUrl: ?string, pollDelayMs: ?number,
 *            unavailableText: ?string}}
 *          `state` is one of `hidden`, `loading`, `notEligible`,
 *          `notRequested`, `requesting`, `processing`, `available`, `failed`
 *          or `unavailable`. `card` is the form of the card the state is shown
 *          in (`request`, `preparing` or `failed`), or null for a state shown
 *          as a single line or as the player. `text` is the card's muted line,
 *          or the single line itself. `pollDelayMs` is set only while
 *          processing.
 */
export function resolveWhiteBalanceView(input, nowMs) {
  const flags = isObject(input) ? input : {};

  if (flags.notFound === true) {
    return view("hidden");
  }

  let current;
  if (flags.body == null) {
    // With nothing read yet there is nothing to keep on screen, so a failed
    // first read is the whole of what the block says.
    current =
      flags.readFailed === true
        ? view("unavailable")
        : view("loading", { text: LOADING_TEXT });
  } else {
    current = stateFromBody(flags.body, nowMs);
  }

  // The card keeps its form and wording while the request is in flight, so
  // the block does not change shape between the press and the answer.
  if (flags.requesting === true && current.button) {
    current = Object.assign(current, {
      state: "requesting",
      button: { label: REQUESTING_BUTTON, disabled: true },
    });
  }

  // A failed read leaves the last answer standing, polling included: the next
  // read may well succeed, and blanking the block would hide a conversion
  // that is still running.
  if (flags.readFailed === true && current.state !== "hidden") {
    current.unavailableText = STATUS_UNAVAILABLE_TEXT;
  }
  return current;
}

// Only a 404 hides the block: it is what a backend without the feature
// answers. Anything else is a read that may succeed next time.
export function readFailureHidesBlock(rejection) {
  return isObject(rejection) && rejection.status === 404;
}

/**
 * What a refused request leads to.
 *
 * @param {object} rejection The store action's rejection.
 * @returns {{outcome: string, message: ?string}}
 *          `showFreshStatus`: read the status and show what it returns, with
 *          no message. `readAgain`: show `message`, then read the status.
 *          `keepPrevious`: show `message` and leave the block as it was.
 *          `message` is the backend's first message, or null if it sent none.
 */
export function resolveRequestFailure(rejection) {
  const err = isObject(rejection) ? rejection : {};
  const messages = Array.isArray(err.messages) ? err.messages : [];
  const message = usableText(messages[0]);

  // The refusal says why the test may not be converted but does not carry
  // the test's conversion. Built from the refusal alone, the block would
  // drop a finished video or a failure the backend still holds, so it shows
  // what a status read returns.
  if (err.status === 400 && isObject(err.data) && err.data.eligible === false) {
    return { outcome: "showFreshStatus", message: null };
  }
  // A collision with another request for the same test; the status read
  // shows whatever that request produced.
  if (err.status === 409) {
    return { outcome: "readAgain", message: message };
  }
  return { outcome: "keepPrevious", message: message };
}

/**
 * Whether a change between two status bodies calls for the ready or failed
 * notice. Only a conversion this page saw processing counts: a test opened
 * with its conversion already final has nothing new to announce.
 *
 * @param {?object} previousBody The body shown before, or null.
 * @param {?object} nextBody     The body just received.
 * @returns {?{kind: string, title: string, text: string}} `kind` is `ready`
 *          or `failed`; `text` is the notice's line under its title.
 */
export function resolveNotice(previousBody, nextBody) {
  if (previousBody == null || nextBody == null) {
    return null;
  }
  if (stateFromBody(previousBody, NaN).state !== "processing") {
    return null;
  }
  const nextData = isObject(nextBody) && isObject(nextBody.data) ? nextBody.data : {};
  const nextConversion = isObject(nextData.conversion) ? nextData.conversion : {};
  if (FINAL_STATUSES.indexOf(nextConversion.status) === -1) {
    return null;
  }

  // Announce what the block now shows: a success with no video displays as a
  // failure.
  const next = stateFromBody(nextBody, NaN).state;
  if (next === "available") {
    return { kind: "ready", title: NOTICE_READY_TITLE, text: NOTICE_READY_TEXT };
  }
  if (next === "failed") {
    return { kind: "failed", title: NOTICE_FAILED_TITLE, text: NOTICE_FAILED_TEXT };
  }
  return null;
}
